import { AnyAction } from 'redux'
import { produce } from 'immer'
import { find, isEqual, reject } from 'lodash'
import { nanoid } from 'nanoid'
import { Node } from 'konva/lib/Node'
import labelStageService from '../../services/labelStage.service'

export type Type = 'inventoryPallet'
  | 'inventoryCase'
  |'receivingPallet'
  | 'receivingCase'
  | 'customLabel';

export type StageUnit = 'in' | 'mm' | 'px';
export type GridVisibility = 'on' | 'off';

export type FontStyle = 'normal' | 'bold' | 'italic' | 'italic bold'
export type TextAlign = 'left' | 'center' | 'right'

export interface ImageElement {
  id: string;
  type: 'image';
  url: string;
  width: number;
  height: number;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
}

export interface TextElement {
  id: string;
  type: 'text';
  text: string;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  align: TextAlign;
  font: {
    family: string;
    size: number;
    style: FontStyle;
    color: string;
  };
  opacity: number;
}

export interface BarcodeElement {
  id: string;
  type: 'barcode';
  text: string;
  format: string;
  url: string;
  width: number;
  height: number;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
}

export interface LineElement {
  id: string;
  type: 'line';
  points: number[];
  stroke: string;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
  width: number;
  height: number;
}

export interface CircleElement {
  id: string;
  type: 'circle';
  radius: number,
  fill: string,
  stroke: string,
  strokeWidth: number
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
}

export interface RectElement {
  id: string;
  type: 'rect';
  fill: string,
  stroke: string,
  strokeWidth: number
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  width: number;
  height: number;
  opacity: number;
}

export interface EllipseElement {
  id: string;
  type: 'ellipse';
  radiusX: number,
  radiusY: number,
  fill: string,
  stroke: string,
  strokeWidth: number
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
}

export interface WedgeElement {
  id: string;
  type: 'wedge';
  radius: number,
  fill: string,
  stroke: string,
  strokeWidth: number
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  angle: number;
  opacity: number;
}

export interface RingElement {
  id: string;
  type: 'ring';
  fill: string,
  stroke: string,
  strokeWidth: number
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  outerRadius: number;
  innerRadius: number;
  opacity: number;
}

export interface ArcElement {
  angle: number;
  outerRadius: number;
  innerRadius: number;
  id: string;
  type: 'arc';
  fill: string;
  stroke: string;
  strokeWidth: number
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
}

export interface ArrowElement {
  id: string;
  type: 'arrow';
  fill: string,
  stroke: string,
  strokeWidth: number
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  points: number[];
  pointerLength: number;
  pointerWidth: number;
  opacity: number;
}

export type Element = ImageElement | TextElement | BarcodeElement |
 LineElement | CircleElement | RectElement | EllipseElement | WedgeElement
 | RingElement | ArcElement  | ArrowElement;

interface History {
  stack: Element[][],
  pointer: number | null
}

export interface Markup {
  target: 'site' | 'contact' | 'item' | 'customLabel' | 'category'
  type: Type | null
  contactId: number | null
  itemId: number | null
  categoryId: number | null
  customLabelId: number | null
  zoom: number
  stage: {
    unit: StageUnit;
    width: number;
    height: number;
    grid: {
      size: number;
      visibility: GridVisibility;
    };
  };
  elements: Element[];
  selectedElement: string | null;
}

export interface State {
  id: number | null
  markup: Markup
  active: boolean | null
  cloning: boolean,
  history: History
}

const initialState: State = {
  id: null,
  markup: {
    target: 'site',
    type: null,
    contactId: null,
    itemId: null,
    categoryId: null,
    customLabelId: null,
    zoom: 1,
    stage: {
      unit: 'in',
      width: 6,
      height: 4,
      grid: {
        size: 48,
        visibility: 'on'
      }
    },
    elements: [],
    selectedElement: null
  },
  active: null,
  cloning: false,
  history: {
    stack: [],
    pointer: null
  }
}

export const actions = {
  resetState: 'labelBuilder/resetState',
  replaceId: 'labelBuilder/replaceId',
  resetId: 'labelBuilder/resetId',
  replaceMarkup: 'labelBuilder/replaceMarkup',
  replaceMarkupTarget: 'labelBuilder/replaceMarkupTarget',
  replaceMarkupType: 'labelBuilder/replaceMarkupType',
  resetMarkupType: 'labelBuilder/resetMarkupType',
  replaceMarkupContactId: 'labelBuilder/replaceMarkupContactId',
  resetMarkupContactId: 'labelBuilder/resetMarkupContactId',
  replaceMarkupItemId: 'labelBuilder/replaceMarkupItemId',
  replaceMarkupCategoryId: 'labelBuilder/replaceMarkupCategoryId',
  resetMarkupItemId: 'labelBuilder/resetMarkupItemId',
  resetMarkupCategoryId: 'labelBuilder/resetMarkupCategoryId',
  replaceMarkupCustomLabelId: 'labelBuilder/replaceMarkupCustomLabelId',
  resetMarkupCustomLabelId: 'labelBuilder/resetMarkupCustomLabelId',
  decrementZoom: 'labelBuilder/decrementZoom',
  incrementZoom: 'labelBuilder/incrementZoom',
  replaceStageUnit: 'labelBuilder/replaceStageUnit',
  replaceStageWidth: 'labelBuilder/replaceStageWidth',
  replaceStageHeight: 'labelBuilder/replaceStageHeight',
  resetStage: 'labelBuilder/resetStage',
  resetStageSize: 'labelBuilder/resetStageSize',
  replaceGridSize: 'labelBuilder/replaceGridSize',
  replaceGridVisibility: 'labelBuilder/replaceGridVisibility',
  resetElements: 'labelBuilder/resetElements',
  replaceElements: 'labelBuilder/replaceElements',
  addImageElement: 'labelBuilder/addImageElement',
  replaceImageElementImage: 'labelBuilder/replaceImageElementImage',
  addTextElement: 'labelBuilder/addTextElement',
  addBarcodeElement: 'labelBuilder/addBarcodeElement',
  addLineElement: 'labelBuilder/addLineElement',
  addCircleElement: 'labelBuilder/addCircleElement',
  addRectElement: 'labelBuilder/addRectElement',
  addWedgeElement: 'labelBuilder/addWedgeElement',
  addEllipseElement: 'labelBuilder/addEllipseElement',
  addRingElement: 'labelBuilder/addRingElement',
  addArcElement: 'labelBuilder/addArcElement',
  addArrowElement: 'labelBuilder/addArrowElement',
  replaceBarcodeElementFormat: 'labelBuilder/replaceBarcodeElementFormat',
  replaceBarcodeElementImage: 'labelBuilder/replaceBarcodeElementImage',
  replaceElementText: 'labelBuilder/replaceElementText',
  appendElementText: 'labelBuilder/appendElementText',
  replaceTextElementFontFamily: 'labelBuilder/replaceTextElementFontFamily',
  replaceTextElementFontSize: 'labelBuilder/replaceTextElementFontSize',
  replaceTextElementFontStyle: 'labelBuilder/replaceTextElementFontStyle',
  replaceTextElementAlign: 'labelBuilder/replaceTextElementAlign',
  replaceTextElementColor: 'labelBuilder/replaceTextElementColor',
  replaceLineElementStroke: 'labelBuilder/replaceLineElementStroke',
  replaceCircleElementStroke: 'labelBuilder/replaceCircleElementStroke',
  replaceCircleElementFill: 'labelBuilder/replaceCircleElementFill',
  replaceRectElementStroke: 'labelBuilder/replaceRectElementStroke',
  replaceRectElementFill: 'labelBuilder/replaceRectElementFill',
  replaceEllipseElementStroke: 'labelBuilder/replaceEllipseElementStroke',
  replaceEllipseElementFill: 'labelBuilder/replaceEllipseElementFill',
  replaceWedgeElementStroke: 'labelBuilder/replaceWedgeElementStroke',
  replaceWedgeElementFill: 'labelBuilder/replaceWedgeElementFill',
  replaceRingElementStroke: 'labelBuilder/replaceRingElementStroke',
  replaceRingElementFill: 'labelBuilder/replaceRingElementFill',
  replaceArcElementStroke: 'labelBuilder/replaceArcElementStroke',
  replaceArcElementFill: 'labelBuilder/replaceArcElementFill',
  replaceArrowElementStroke: 'labelBuilder/replaceArrowElementStroke',
  replaceArrowElementFill: 'labelBuilder/replaceArrowElementFill',
  transformElement: 'labelBuilder/transformElement',
  dragElement: 'labelBuilder/dragElement',
  moveElementLayerToTop: 'labelBuilder/moveElementLayerToTop',
  moveElementLayerToBottom: 'labelBuilder/moveElementLayerToBottom',
  replaceSelectedElement: 'labelBuilder/replaceSelectedElement',
  clearSelectedElement: 'labelBuilder/clearSelectedElement',
  resetSelectedElement: 'labelBuilder/resetSelectedElement',
  moveSelectedElement: 'labelBuilder/moveSelectedElement',
  replaceActive: 'labelBuilder/replaceActive',
  resetActive: 'labelBuilder/resetActive',
  replaceCloning: 'labelBuilder/replaceCloning',
  resetHistory: 'labelBuilder/resetHistory',
  recordHistory: 'labelBuilder/recordHistory',
  undoHistory: 'labelBuilder/undoHistory',
  redoHistory: 'labelBuilder/redoHistory',
  replaceArcOpacity: 'labelBuilder/replaceArcOpacity',
  replaceArrowOpacity: 'labelBuilder/replaceArrowOpacity',
  replaceBarcodeOpacity: 'labelBuilder/replaceBarcodeOpacity',
  replaceCircleOpacity: 'labelBuilder/replaceCircleOpacity',
  replaceEllipseOpacity: 'labelBuilder/replaceEllipseOpacity',
  replaceImageOpacity: 'labelBuilder/replaceImageOpacity',
  replaceLineOpacity: 'labelBuilder/replaceLineOpacity',
  replaceRectOpacity: 'labelBuilder/replaceRectOpacity',
  replaceRingOpacity: 'labelBuilder/replaceRingOpacity',
  replaceTextOpacity: 'labelBuilder/replaceTextOpacity',
  replaceWedgeOpacity: 'labelBuilder/replaceWedgeOpacity',
}

export function resetState () {
  return { type: actions.resetState }
}

export function replaceId (id: number) {
  return { type: actions.replaceId, id }
}

export function resetId () {
  return { type: actions.resetId }
}

export function replaceMarkup (markup: Markup) {
  return { type: actions.replaceMarkup, markup }
}

export function replaceMarkupTarget (target: Markup['target']) {
  return { type: actions.replaceMarkupTarget, target }
}

export function replaceMarkupType (markupType: Markup['type']) {
  return { type: actions.replaceMarkupType, markupType }
}

export function resetMarkupType () {
  return { type: actions.resetMarkupType }
}

export function replaceMarkupContactId (contactId: number) {
  return { type: actions.replaceMarkupContactId, contactId }
}

export function resetMarkupContactId () {
  return { type: actions.resetMarkupContactId }
}

export function replaceMarkupItemId (itemId: number) {
  return { type: actions.replaceMarkupItemId, itemId }
}

export function replaceMarkupCategoryId (categoryId: number) {
  return { type: actions.replaceMarkupCategoryId, categoryId }
}

export function resetMarkupItemId () {
  return { type: actions.resetMarkupItemId }
}

export function resetMarkupCategoryId () {
  return { type: actions.resetMarkupCategoryId }
}

export function replaceMarkupCustomLabelId (customLabelId: number) {
  return { type: actions.replaceMarkupCustomLabelId, customLabelId }
}

export function resetMarkupCustomLabelId () {
  return { type: actions.resetMarkupCustomLabelId }
}

export function decrementZoom () {
  return { type: actions.decrementZoom }
}

export function incrementZoom () {
  return { type: actions.incrementZoom }
}

export function replaceStageUnit (unit: StageUnit) {
  return { type: actions.replaceStageUnit, unit }
}

export function replaceStageWidth (width: number) {
  return { type: actions.replaceStageWidth, width }
}

export function replaceStageHeight (height: number) {
  return { type: actions.replaceStageHeight, height }
}

export function resetStage () {
  return { type: actions.resetStage }
}

export function resetStageSize (unit: StageUnit) {
  return { type: actions.resetStageSize, unit }
}

export function replaceGridSize (size: number) {
  return { type: actions.replaceGridSize, size }
}

export function replaceGridVisibility (visibility: GridVisibility) {
  return { type: actions.replaceGridVisibility, visibility }
}

export function resetElements () {
  return { type: actions.resetElements }
}

export function replaceElements (elements: Element[]) {
  return { type: actions.replaceElements, elements }
}

export function addImageElement (image: HTMLImageElement) {
  return { type: actions.addImageElement, image }
}

export function replaceImageElementImage (id: string, image: HTMLImageElement) {
  return { type: actions.replaceImageElementImage, id, image }
}

export function addTextElement () {
  return { type: actions.addTextElement }
}

export function addBarcodeElement (image: HTMLImageElement) {
  return { type: actions.addBarcodeElement, image }
}

export function addLineElement () {
  return { type: actions.addLineElement }
}

export function addCircleElement () {
  return { type: actions.addCircleElement }
}

export function addRectElement () {
  return { type: actions.addRectElement }
}

export function addEllipseElement () {
  return { type: actions.addEllipseElement }
}

export function addWedgeElement () {
  return { type: actions.addWedgeElement }
}

export function addRingElement () {
  return { type: actions.addRingElement }
}

export function addArcElement () {
  return { type: actions.addArcElement }
}

export function addArrowElement () {
  return { type: actions.addArrowElement }
}

export function replaceBarcodeElementFormat (id: string, format: string) {
  return { type: actions.replaceBarcodeElementFormat, id, format }
}

export function replaceBarcodeElementImage (id: string, image: HTMLImageElement) {
  return { type: actions.replaceBarcodeElementImage, id, image }
}

export function replaceElementText (id: string, text: string) {
  return { type: actions.replaceElementText, id, text }
}

export function appendElementText (id: string, text: string) {
  return { type: actions.appendElementText, id, text }
}

export function replaceTextElementFontFamily (id: string, family: string) {
  return { type: actions.replaceTextElementFontFamily, id, family }
}

export function replaceTextElementFontSize (id: string, size: number) {
  return { type: actions.replaceTextElementFontSize, id, size }
}

export function replaceTextElementFontStyle (id: string, style: FontStyle) {
  return { type: actions.replaceTextElementFontStyle, id, style }
}

export function replaceTextElementAlign (id: string, align: TextAlign) {
  return { type: actions.replaceTextElementAlign, id, align }
}

export function replaceTextElementColor (id: string, color: string) {
  return { type: actions.replaceTextElementColor, id, color }
}

export function replaceLineElementStroke (id: string, stroke: string) {
  return { type: actions.replaceLineElementStroke, id, stroke }
}

export function replaceCircleElementStroke (id: string, stroke: string) {
  return { type: actions.replaceCircleElementStroke, id, stroke }
}

export function replaceArcOpacity (id: string, opacity: number) {
  return { type: actions.replaceArcOpacity, id, opacity }
}

export function replaceArrowOpacity (id: string, opacity: number) {
  return { type: actions.replaceArrowOpacity, id, opacity }
}

export function replaceBarcodeOpacity (id: string, opacity: number) {
  return { type: actions.replaceBarcodeOpacity, id, opacity }
}

export function replaceCircleOpacity (id: string, opacity: number) {
  return { type: actions.replaceCircleOpacity, id, opacity }
}

export function replaceEllipseOpacity (id: string, opacity: number) {
  return { type: actions.replaceEllipseOpacity, id, opacity }
}

export function replaceImageOpacity (id: string, opacity: number) {
  return { type: actions.replaceImageOpacity, id, opacity }
}

export function replaceLineOpacity (id: string, opacity: number) {
  return { type: actions.replaceLineOpacity, id, opacity }
}

export function replaceRectOpacity (id: string, opacity: number) {
  return { type: actions.replaceRectOpacity, id, opacity }
}

export function replaceRingOpacity (id: string, opacity: number) {
  return { type: actions.replaceRingOpacity, id, opacity }
}

export function replaceTextOpacity (id: string, opacity: number) {
  return { type: actions.replaceTextOpacity, id, opacity }
}

export function replaceWedgeOpacity (id: string, opacity: number) {
  return { type: actions.replaceWedgeOpacity, id, opacity }
}

export function replaceCircleElementFill (id: string, fill: string) {
  return { type: actions.replaceCircleElementFill, id, fill }
}

export function replaceRectElementStroke (id: string, stroke: string) {
  return { type: actions.replaceRectElementStroke, id, stroke }
}

export function replaceRectElementFill (id: string, fill: string) {
  return { type: actions.replaceRectElementFill, id, fill }
}

export function replaceEllipseElementStroke (id: string, stroke: string) {
  return { type: actions.replaceEllipseElementStroke, id, stroke }
}

export function replaceEllipseElementFill (id: string, fill: string) {
  return { type: actions.replaceEllipseElementFill, id, fill }
}

export function replaceWedgeElementStroke (id: string, stroke: string) {
  return { type: actions.replaceWedgeElementStroke, id, stroke }
}

export function replaceWedgeElementFill (id: string, fill: string) {
  return { type: actions.replaceWedgeElementFill, id, fill }
}

export function replaceRingElementStroke (id: string, stroke: string) {
  return { type: actions.replaceRingElementStroke, id, stroke }
}

export function replaceRingElementFill (id: string, fill: string) {
  return { type: actions.replaceRingElementFill, id, fill }
}

export function replaceArcElementStroke (id: string, stroke: string) {
  return { type: actions.replaceArcElementStroke, id, stroke }
}

export function replaceArcElementFill (id: string, fill: string) {
  return { type: actions.replaceArcElementFill, id, fill }
}

export function replaceArrowElementStroke (id: string, stroke: string) {
  return { type: actions.replaceArrowElementStroke, id, stroke }
}

export function replaceArrowElementFill (id: string, fill: string) {
  return { type: actions.replaceArrowElementFill, id, fill }
}

export function transformElement (element: Node) {
  return { type: actions.transformElement, element }
}

export function dragElement (element: Node) {
  return { type: actions.dragElement, element }
}

export function moveElementLayerToTop (element: Node) {
  return { type: actions.moveElementLayerToTop, element }
}

export function moveElementLayerToBottom (element: Node) {
  return { type: actions.moveElementLayerToBottom, element }
}

export function replaceSelectedElement (element: Node) {
  return { type: actions.replaceSelectedElement, element }
}

export function resetSelectedElement () {
  return { type: actions.resetSelectedElement }
}

export function clearSelectedElement () {
  return { type: actions.clearSelectedElement }
}

export function moveSelectedElement (x: number, y: number) {
  return { type: actions.moveSelectedElement, x, y }
}

export function replaceActive (active: boolean) {
  return { type: actions.replaceActive, active }
}

export function resetActive () {
  return { type: actions.resetActive }
}

export function replaceCloning (cloning: boolean) {
  return { type: actions.replaceCloning, cloning }
}

export function resetHistory () {
  return { type: actions.resetHistory }
}

export function recordHistory (elements: Element[]) {
  return { type: actions.recordHistory, elements }
}

export function undoHistory () {
  return { type: actions.undoHistory }
}

export function redoHistory () {
  return { type: actions.redoHistory }
}

const moveElementToStart = <T>(arr: T[], index: number): T[] => {
  if (index < 0 || index >= arr.length) {
    throw new Error("Index out of bounds");
  }

  const element = arr[index];
  const newArr = [element, ...arr.slice(0, index), ...arr.slice(index + 1)];

  return newArr;
};

const moveElementToEnd = <T>(arr: T[], index: number): T[] => {
  if (index < 0 || index >= arr.length) {
    throw new Error("Index out of bounds");
  }

  const element = arr[index];
  const newArr = [...arr.slice(0, index), ...arr.slice(index + 1), element];

  return newArr;
};

const findElementIndex = <T>(arr: T[], predicate: (value: T) => boolean): number => {
  return arr.findIndex(predicate);
};

export default function reducers (state = initialState, action: AnyAction) {
  switch (action.type) {
    case actions.resetState:
      return initialState
    case actions.replaceId:
      return produce(state, draft => {
        draft.id = action.id
      })
    case actions.resetId:
      return produce(state, draft => {
        draft.id = null
      })
    case actions.replaceMarkup:
      return produce(state, draft => {
        draft.markup = action.markup
      })
    case actions.replaceMarkupTarget:
      return produce(state, draft => {
        draft.markup.target = action.target
        if (action.target === 'customLabel') {
          draft.markup.type = null
        }
      })
    case actions.replaceMarkupType:
      return produce(state, draft => {
        draft.markup.type = action.markupType
      })
    case actions.resetMarkupType:
      return produce(state, draft => {
        draft.markup.type = null
      })
    case actions.replaceMarkupContactId:
      return produce(state, draft => {
        draft.markup.contactId = action.contactId
      })
    case actions.resetMarkupContactId:
      return produce(state, draft => {
        draft.markup.contactId = null
      })
    case actions.replaceMarkupItemId:
      return produce(state, draft => {
        draft.markup.itemId = action.itemId
      })
    case actions.replaceMarkupCategoryId:
      return produce(state, draft => {
        draft.markup.categoryId = action.categoryId
      })  
    case actions.resetMarkupItemId:
      return produce(state, draft => {
        draft.markup.itemId = null
      })
    case actions.resetMarkupCategoryId:
      return produce(state, draft => {
        draft.markup.categoryId = null
      })  
    case actions.replaceMarkupCustomLabelId:
      return produce(state, draft => {
        draft.markup.customLabelId = action.customLabelId
      })
    case actions.resetMarkupCustomLabelId:
      return produce(state, draft => {
        draft.markup.customLabelId = null
      })
    case actions.decrementZoom:
      return produce(state, draft => {
        draft.markup.zoom -= 0.1
      })
    case actions.incrementZoom:
      return produce(state, draft => {
        draft.markup.zoom += 0.1
      })
    case actions.replaceStageUnit:
      return produce(state, draft => {
        draft.markup.stage.unit = action.unit
      })
    case actions.replaceStageWidth:
      return produce(state, draft => {
        draft.markup.stage.width = action.width
      })
    case actions.replaceStageHeight:
      return produce(state, draft => {
        draft.markup.stage.height = action.height
      })
    case actions.resetStage: {
      return produce(state, draft => {
        draft.markup.stage = initialState.markup.stage
      })
    }
    case actions.resetStageSize: {
      const unit = action.unit as StageUnit
      return produce(state, draft => {
        switch (unit) {
          case 'px': {
            draft.markup.stage.width = 576
            draft.markup.stage.height = 384
            return draft
          }
          case 'in': {
            draft.markup.stage.width = 6
            draft.markup.stage.height = 4
            return draft
          }
          case 'mm': {
            draft.markup.stage.width = 152.4
            draft.markup.stage.height = 101.6
            return draft
          }
        }
      })
    }
    case actions.replaceGridSize:
      return produce(state, draft => {
        draft.markup.stage.grid.size = action.size
      })
    case actions.replaceGridVisibility:
      return produce(state, draft => {
        draft.markup.stage.grid.visibility = action.visibility
      })
    case actions.resetElements:
      return produce(state, draft => {
        draft.markup.elements = []
      })
    case actions.replaceElements:
      return produce(state, draft => {
        const elements = action.elements as Element[]
        draft.markup.elements = elements
      })
    case actions.addImageElement: {
      const id = nanoid()
      const image = action.image as HTMLImageElement
      return produce(state, draft => {
        const imageElement: ImageElement = {
          id,
          type: 'image',
          url: image.src,
          width: image.width / 2,
          height: image.height / 2,
          x: 10,
          y: 10,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
          opacity: 1
        }
        draft.markup.elements.push(imageElement)
      })
    }
    case actions.replaceImageElementImage: {
      const id = action.id as string
      const image = action.image as HTMLImageElement
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'image') return
        const scaledImage = labelStageService.scaleImageToStage(draft.markup.stage, image)
        element.url = image.src
        element.width = scaledImage.width
        element.height = scaledImage.height
      })
    }
    case actions.addTextElement: {
      const id = nanoid()
      return produce(state, draft => {
        const text: TextElement = {
          id,
          type: 'text',
          text: 'Text',
          x: 10,
          y: 10,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
          align: 'left',
          opacity: 1,
          font: {
            family: 'arial',
            size: 18,
            style: 'normal',
            color: '#000000'
          }
        }
        draft.markup.elements.push(text)
      })
    }
    case actions.addBarcodeElement: {
      const id = nanoid()
      const image = action.image as HTMLImageElement
      return produce(state, draft => {
        const barcode: BarcodeElement = {
          id,
          type: 'barcode',
          text: '',
          format: '',
          url: image.src,
          width: image.width,
          height: image.height,
          x: 10,
          y: 10,
          scaleX: 1 / 2,
          scaleY: 1 / 2,
          rotation: 0,
          opacity: 1
        }
        draft.markup.elements.push(barcode)
      })
    }
    case actions.addLineElement: {
      const id = nanoid()
      return produce(state, draft => {
        const line: LineElement = {
          id,
          type: 'line',
          points: [10, 10, 100, 10],
          stroke: '#000000',
          x: 10,
          y: 10,
          scaleX: 3 / 3,
          scaleY: 3 / 3,
          rotation: 0,
          opacity: 1,
          width: 50,
          height: 50
        }
        draft.markup.elements.push(line)
      })
    }
    case actions.addCircleElement: {
      const id = nanoid()
      return produce(state, draft => {
        const circle: CircleElement = {
          id,
          type: 'circle',
          radius: 10,
          fill: 'white',
          stroke: 'black',
          strokeWidth: 1,
          x: 10,
          y: 10,
          scaleX: 2 / 3,
          scaleY: 2 / 3,
          rotation: 0,
          opacity: 1
        }
        draft.markup.elements.push(circle)
      })
    }
    case actions.addRectElement: {
      const id = nanoid()
      return produce(state, draft => {
        const rect: RectElement = {
          id,
          type: 'rect',
          fill: 'white',
          stroke: 'black',
          strokeWidth: 1,
          x: 10,
          y: 10,
          scaleX: 2 / 3,
          scaleY: 2 / 3,
          rotation: 0,
          width: 50,
          height: 100,
          opacity: 1
        }
        draft.markup.elements.push(rect)
      })
    }
    case actions.addEllipseElement: {
      const id = nanoid()
      return produce(state, draft => {
        const ellipse: EllipseElement = {
          id,
          type: 'ellipse',
          radiusX: 20,
          radiusY: 30,
          fill: 'white',
          stroke: 'black',
          strokeWidth: 1,
          x: 10,
          y: 10,
          scaleX: 2 / 3,
          scaleY: 2 / 3,
          rotation: 0,
          opacity: 1
        }
        draft.markup.elements.push(ellipse)
      })
    }
    case actions.addWedgeElement: {
      const id = nanoid()
      return produce(state, draft => {
        const wedge: WedgeElement = {
          angle: 60,
          id,
          type: 'wedge',
          radius: 70,
          fill: 'white',
          stroke: 'black',
          strokeWidth: 1,
          x: 50,
          y: 90,
          scaleX: 1,
          scaleY: 1,
          rotation: -120,
          opacity: 1
        }
        draft.markup.elements.push(wedge)
      })
    }
    case actions.addRingElement: {
      const id = nanoid()
      return produce(state, draft => {
        const ring: RingElement = {
          id,
          type: 'ring',
          fill: 'white',
          stroke: 'black',
          strokeWidth: 1,
          x: 50,
          y: 90,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
          innerRadius: 10,
          outerRadius: 20,
          opacity: 1
        }
        draft.markup.elements.push(ring)
      })
    }
    case actions.addArcElement: {
      const id = nanoid()
      return produce(state, draft => {
        const arc: ArcElement = {
          id,
          type: 'arc',
          fill: 'white',
          stroke: 'black',
          strokeWidth: 1,
          x: 50,
          y: 90,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
          innerRadius: 40,
          outerRadius: 70,
          angle: 60,
          opacity: 1
        }
        draft.markup.elements.push(arc)
      })
    }
    case actions.addArrowElement: {
      const id = nanoid()
      return produce(state, draft => {
        const arrow: ArrowElement = {
          id,
          type: 'arrow',
          fill: 'white',
          stroke: 'black',
          strokeWidth: 1,
          x: 50,
          y: 90,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
          points: [0, 0, 10 / 2, 50 / 2],
          pointerLength: 5,
          pointerWidth: 5,
          opacity: 1
        }
        draft.markup.elements.push(arrow)
      })
    }
    case actions.replaceBarcodeElementFormat: {
      const id = action.id as string
      const format = action.format as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'barcode') return
        element.format = format
      })
    }
    case actions.replaceBarcodeElementImage: {
      const id = action.id as string
      const image = action.image as HTMLImageElement
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'barcode') return
        const scaledImage = labelStageService.scaleImageToStage(draft.markup.stage, image)
        element.url = image.src
        element.width = scaledImage.width
        element.height = scaledImage.height
      })
    }
    case actions.replaceElementText: {
      const id = action.id as string
      const text = action.text as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type === 'image' || element.type === 'line') return
        element.text = text
      })
    }
    case actions.appendElementText: {
      const id = action.id as string
      const text = action.text as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type === 'image' || element.type === 'line') return
        element.text = `${element.text} ${text}`
      })
    }
    case actions.replaceTextElementFontFamily: {
      const id = action.id as string
      const family = action.family as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'text') return
        element.font.family = family
      })
    }
    case actions.replaceTextElementFontSize: {
      const id = action.id as string
      const size = action.size
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'text') return
        element.font.size = size
      })
    }
    case actions.replaceTextElementFontStyle: {
      const id = action.id as string
      const style = action.style as FontStyle
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'text') return
        element.font.style = style
      })
    }
    case actions.replaceTextElementAlign: {
      const id = action.id as string
      const align = action.align
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'text') return
        element.align = align
      })
    }
    case actions.replaceTextElementColor: {
      const id = action.id as string
      const color = action.color as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'text') return
        element.font.color = color
      })
    }
    case actions.replaceLineElementStroke: {
      const id = action.id as string
      const stroke = action.stroke as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'line') return
        element.stroke = stroke
      })
    }
    case actions.replaceCircleElementStroke: {
      const id = action.id as string
      const stroke = action.stroke as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'circle') return
        element.stroke = stroke
      })
    }

    case actions.replaceArcOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'arc') return
        element.opacity = opacity
      })
    }
    case actions.replaceArrowOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'arrow') return
        element.opacity = opacity
      })
    }
    case actions.replaceBarcodeOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'barcode') return
        element.opacity = opacity
      })
    }
    case actions.replaceCircleOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'circle') return
        element.opacity = opacity
      })
    }

    case actions.replaceEllipseOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'ellipse') return
        element.opacity = opacity
      })
    }

    case actions.replaceImageOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'image') return
        element.opacity = opacity
      })
    }

    case actions.replaceLineOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'line') return
        element.opacity = opacity
      })
    }

    case actions.replaceRectOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'rect') return
        element.opacity = opacity
      })
    }

    case actions.replaceRingOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'ring') return
        element.opacity = opacity
      })
    }

    case actions.replaceTextOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'text') return
        element.opacity = opacity
      })
    }

    case actions.replaceWedgeOpacity: {
      const id = action.id as string
      const opacity = action.opacity as number
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'wedge') return
        element.opacity = opacity
      })
    }

    case actions.replaceCircleElementFill: {
      const id = action.id as string
      const fill = action.fill as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'circle') return
        element.fill = fill
      })
    }

    case actions.replaceRectElementStroke: {
      const id = action.id as string
      const stroke = action.stroke as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'rect') return
        element.stroke = stroke
      })
    }
    case actions.replaceRectElementFill: {
      const id = action.id as string
      const fill = action.fill as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'rect') return
        element.fill = fill
      })
    }

    case actions.replaceEllipseElementStroke: {
      const id = action.id as string
      const stroke = action.stroke as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'ellipse') return
        element.stroke = stroke
      })
    }
    case actions.replaceEllipseElementFill: {
      const id = action.id as string
      const fill = action.fill as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'ellipse') return
        element.fill = fill
      })
    }

    case actions.replaceWedgeElementStroke: {
      const id = action.id as string
      const stroke = action.stroke as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'wedge') return
        element.stroke = stroke
      })
    }
    case actions.replaceWedgeElementFill: {
      const id = action.id as string
      const fill = action.fill as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'wedge') return
        element.fill = fill
      })
    }

    case actions.replaceRingElementStroke: {
      const id = action.id as string
      const stroke = action.stroke as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'ring') return
        element.stroke = stroke
      })
    }
    case actions.replaceRingElementFill: {
      const id = action.id as string
      const fill = action.fill as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'ring') return
        element.fill = fill
      })
    }
    case actions.replaceArcElementStroke: {
      const id = action.id as string
      const stroke = action.stroke as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'arc') return
        element.stroke = stroke
      })
    }
    case actions.replaceArcElementFill: {
      const id = action.id as string
      const fill = action.fill as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'arc') return
        element.fill = fill
      })
    }

    case actions.replaceArrowElementStroke: {
      const id = action.id as string
      const stroke = action.stroke as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'arrow') return
        element.stroke = stroke
      })
    }
    case actions.replaceArrowElementFill: {
      const id = action.id as string
      const fill = action.fill as string
      return produce(state, draft => {
        const element = find(draft.markup.elements, element => {
          return element.id === id
        })
        if (!element || element.type !== 'arrow') return
        element.fill = fill
      })
    }

    /*case actions.transformElement:
      return produce(state, draft => {
        const elementNode = action.element
        const element = find(
          draft.markup.elements,
          (element) => element.id === elementNode.getAttr('id')
        )
        if (element) {
          element.scaleX = elementNode.getAttr('scaleX')
          element.scaleY = elementNode.getAttr('scaleY')
          element.x = elementNode.getAttr('x')
          element.y = elementNode.getAttr('y')
          element.rotation = elementNode.getAttr('rotation')
        }
      })*/
    case actions.transformElement:
      return produce(state, draft => {
        const elementNode = action.element;
        const element = find(
          draft.markup.elements,
          (element) => element.id === elementNode.getAttr('id')
        );
    
        if (element) {
          if (element.type === 'text') {
            // Apply wrapping logic for text elements
            const newWidth = elementNode.width() * elementNode.scaleX();
    
            // Update text element properties
            element.width = newWidth;
            element.height = elementNode.height(); // Optional, to handle height based on wrapping
            element.x = elementNode.getAttr('x');
            element.y = elementNode.getAttr('y');
            element.rotation = elementNode.getAttr('rotation');
    
            // Reset scaleX and scaleY to 1 for text wrapping
            element.scaleX = 1;
            element.scaleY = 1;
    
            // Update Konva node with new width and reset scaling
            elementNode.width(newWidth);
            elementNode.scaleX(1);
            elementNode.scaleY(1);
          } else {
            // Keep original logic for non-text elements
            element.scaleX = elementNode.getAttr('scaleX');
            element.scaleY = elementNode.getAttr('scaleY');
            element.x = elementNode.getAttr('x');
            element.y = elementNode.getAttr('y');
            element.rotation = elementNode.getAttr('rotation');
          }
        }
      })
         
    case actions.dragElement:
      return produce(state, draft => {
        const elementNode = action.element
        const element = find(
          draft.markup.elements,
          (element) => element.id === elementNode.getAttr('id')
        )
        if (element) {
          element.x = elementNode.getAttr('x')
          element.y = elementNode.getAttr('y')
        }
      })
    case actions.moveElementLayerToTop:
      return produce(state, draft => {
          
        const elementNode = action.element

        const elementIndex = findElementIndex(draft.markup.elements,(element) => element.id === elementNode.getAttr('id'))

        var newArr = moveElementToEnd(draft.markup.elements,elementIndex)
        //var newArr = moveElementToStart(draft.markup.elements,elementIndex)
        draft.markup.elements = newArr
      })
    case actions.moveElementLayerToBottom:
      return produce(state, draft => {
          
        const elementNode = action.element

        const elementIndex = findElementIndex(draft.markup.elements,(element) => element.id === elementNode.getAttr('id'))

        var newArr = moveElementToStart(draft.markup.elements,elementIndex)
        //var newArr = moveElementToEnd(draft.markup.elements,elementIndex)
        draft.markup.elements = newArr
      })
    case actions.replaceSelectedElement:
      return produce(state, draft => {
        const elementNode = action.element
        const selectedElement = find(draft.markup.elements, element => {
          return element.id === elementNode.getAttr('id')
        })
        if (!selectedElement) return
        draft.markup.selectedElement = selectedElement.id
      })
    case actions.clearSelectedElement:
      return produce(state, draft => {
        draft.markup.elements = reject(draft.markup.elements, (element) => {
          const { selectedElement } = draft.markup
          if (selectedElement) {
            return element.id === selectedElement
          } else return false
        })
        draft.markup.selectedElement = null
      })
    case actions.resetSelectedElement:
      return produce(state, draft => {
        draft.markup.selectedElement = null
      })
    case actions.moveSelectedElement:
      return produce(state, draft => {
        const xMovePoint = action.x
        const yMovePoint = action.y
        const selectedElement = find(draft.markup.elements, element => {
          return element.id === draft.markup.selectedElement
        })
        if (!selectedElement) return
        selectedElement.x += xMovePoint
        selectedElement.y += yMovePoint
      })
    case actions.replaceActive:
      return produce(state, draft => {
        draft.active = action.active
      })
    case actions.resetActive:
      return produce(state, draft => {
        draft.active = initialState.active
      })
    case actions.replaceCloning:
      return produce(state, draft => {
        draft.cloning = action.cloning
      })
    case actions.resetHistory:
      return produce(state, draft => {
        draft.history = initialState.history
      })
    case actions.recordHistory:
      return produce(state, draft => {
        const elements = action.elements as Element[]
        const { history: { stack, pointer } } = draft
        const exists = find(stack, stackElements => isEqual(stackElements, elements))
        if (exists) return
        if (pointer !== null && pointer !== stack.length - 1) {
          draft.history.stack = stack.slice(0, pointer + 1)
        }
        draft.history.stack.push(elements)
        draft.history.pointer = stack.length - 1
      })
    case actions.undoHistory:
      return produce(state, draft => {
        if (!draft.history.pointer) return
        draft.history.pointer -= 1
      })
    case actions.redoHistory:
      return produce(state, draft => {
        const { history: { stack, pointer } } = draft
        if (draft.history.pointer === null || pointer === stack.length - 1) return
        draft.history.pointer += 1
      })
    default:
      return state
  }
}
